import { getFormConfigRegistry } from '@risksmart-app/shared/forms/formConfigRegistry';
import _ from 'lodash';
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useModulesStore } from 'src/context/moduleContext';
import { useCustomDatasourceHelpers } from 'src/pages/custom-datasources/useCustomDatasourceHelpers';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { AdditionalConditionField } from '../../edit-field-modal/formRegistryService';
import { useRiskSmartForm } from '../customisable-form/RiskSmartFormContext';
import { useCustomisableFormDataContext } from '../customisable-form-data/CustomisableFormDataContext';
import { ConditionalFieldsContext } from './ConditionalFieldsContext';
import {
  buildConditionGraph,
  buildConditionSchemaContext,
  buildFieldConditionGraph,
  getEffectiveFieldConditions,
  getHiddenFields,
  getHiddenSections,
} from './conditionsGraph';

/**
 * Keeps track of which fields are conditionally hidden.
 * Note, needs to be used with a react hook form FormProvider as it needs access to the forms values
 *
 * `additionalConditionFields` lets the form-config preview register
 * session-only fields as condition sources so rules referencing them evaluate
 * before the field is persisted. Live forms omit it.
 */
export const ConditionalFieldsProvider = ({
  children,
  additionalConditionFields,
}: PropsWithChildren<{
  additionalConditionFields?: AdditionalConditionField[];
}>) => {
  const [hiddenFields, setHiddenFields] = useState<Set<string>>(new Set());
  const [hiddenSections, setHiddenSections] = useState<Set<string>>(new Set());
  const customisableData = useCustomisableFormDataContext();
  const { formFieldConfigurations } = customisableData;
  // TODO: possibly get parent type by other means to remove dependency on useRiskSmartForm
  const { parentType } = useRiskSmartForm();

  const { watch } = useFormContext();
  const modules = useModulesStore((s) => s.modules);
  const currentValues = watch();
  const formSectionsEnabled = useIsFeatureFlagEnabled('dynamic_forms');

  const formConfig = parentType
    ? getFormConfigRegistry(formSectionsEnabled, modules)[parentType]
    : undefined;

  const fieldConditionGraph = useMemo(
    () =>
      buildFieldConditionGraph(
        getEffectiveFieldConditions(
          formFieldConfigurations,
          formConfig,
          formSectionsEnabled
        )
      ),
    [formFieldConfigurations, formConfig, formSectionsEnabled]
  );

  // Fields AND sections. `getHiddenSections` walks this to inherit the field
  // cascade; `getHiddenFields` keeps the field-only graph above, because adding
  // section vertices to its ancestor walk would let a section hide a field,
  // which is not a relationship this model has.
  const conditionGraph = useMemo(
    () =>
      buildConditionGraph(
        getEffectiveFieldConditions(
          formFieldConfigurations,
          formConfig,
          formSectionsEnabled
        ),
        customisableData.formSections ?? []
      ),
    [
      formFieldConfigurations,
      formConfig,
      formSectionsEnabled,
      customisableData.formSections,
    ]
  );

  const helpers = useCustomDatasourceHelpers();
  const { customAttributeSchema } = customisableData;

  // Memoize the static schema context so filteringProps and freeTextKeys are not
  // recomputed on every form-value change (they depend on schema, not on field values).
  const schemaContext = useMemo(
    () =>
      parentType
        ? buildConditionSchemaContext({
            formId: parentType,
            customAttributeSchema,
            helpers,
            formSectionsEnabled,
            modules,
            additionalConditionFields,
          })
        : undefined,
    [
      parentType,
      customAttributeSchema,
      helpers,
      formSectionsEnabled,
      modules,
      additionalConditionFields,
    ]
  );

  useEffect(() => {
    if (!parentType) {
      return;
    }

    const hiddenFieldsLatest = getHiddenFields({
      formId: parentType,
      customisableData,
      fieldConditionGraph,
      currentValues,
      helpers,
      modules,
      additionalConditionFields,
      formSectionsEnabled,
      schemaContext,
    });

    const hiddenSectionsLatest = getHiddenSections({
      formId: parentType,
      customisableData,
      currentValues,
      helpers,
      formSectionsEnabled,
      modules,
      additionalConditionFields,
      schemaContext,
      conditionGraph,
      // Computed immediately above from the same values, so the cascade sees
      // this render's hidden set rather than the previous one held in state.
      hiddenFields: hiddenFieldsLatest,
    });

    setHiddenFields((prev) => {
      if (_.isEqual(prev, hiddenFieldsLatest)) {
        return prev;
      }

      return hiddenFieldsLatest;
    });

    setHiddenSections((prev) => {
      if (_.isEqual(prev, hiddenSectionsLatest)) {
        return prev;
      }

      return hiddenSectionsLatest;
    });
  }, [
    fieldConditionGraph,
    conditionGraph,
    parentType,
    customisableData,
    currentValues,
    helpers,
    modules,
    additionalConditionFields,
    formSectionsEnabled,
    schemaContext,
  ]);

  return (
    <ConditionalFieldsContext.Provider value={{ hiddenFields, hiddenSections }}>
      {children}
    </ConditionalFieldsContext.Provider>
  );
};
